import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CommunityLayout } from '../components/CommunityLayout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '../components';
import { toast } from 'react-hot-toast';
import {
  Newspaper,
  Search,
  Plus,
  MessageSquare,
  ThumbsUp,
  Eye,
  User,
  ChevronRight,
  ExternalLink,
  TrendingUp,
  Clock,
  Flame,
  Star,
} from 'lucide-react';
import { newsroomApi, NewsroomArticle, ArticleSortBy } from '../services/newsroomApi';
import { useAuthStore } from '../store/authStore';
import { formatDistanceToNow } from 'date-fns';
import { nl } from 'date-fns/locale';

export const ArticleCard: React.FC<{ article: NewsroomArticle }> = ({ article }) => {
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const internalHref = `/newsroom/artikel/${article.id}`;
  const isExternal = !!article.external_url;

  const [liked, setLiked] = useState(!!article.user_liked);
  const [likesCount, setLikesCount] = useState(article.likes_count);

  useEffect(() => {
    setLiked(!!article.user_liked);
    setLikesCount(article.likes_count);
  }, [article.user_liked, article.likes_count]);

  const likeMutation = useMutation({
    mutationFn: () => newsroomApi.toggleLike(article.id),
    onMutate: () => {
      const newLiked = !liked;
      setLiked(newLiked);
      setLikesCount((prev) => (newLiked ? prev + 1 : prev - 1));
    },
    onError: () => {
      setLiked(!!article.user_liked);
      setLikesCount(article.likes_count);
      toast.error(t('newsroom.error'));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['newsroom-articles'] });
    },
  });

  const handleCardClick = (e: React.MouseEvent<HTMLElement>) => {
    if ((e.target as HTMLElement).closest('a') || (e.target as HTMLElement).closest('button')) return;
    navigate(internalHref);
  };

  return (
    <article
      className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md hover:border-[#0B1F3A] transition-all group cursor-pointer"
      onClick={handleCardClick}
    >
      <Link to={internalHref} tabIndex={-1}>
        <div className="aspect-video overflow-hidden bg-gray-100">
          {article.cover_image_url ? (
            <img
              src={article.cover_image_url}
              alt={article.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/belagom-text.png';
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <img src="/belagom-text.png" alt="" className="w-24 opacity-30" />
            </div>
          )}
        </div>
      </Link>

      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span
            className="text-xs px-2 py-0.5 rounded-full font-medium"
            style={{
              backgroundColor: article.channel_color + '20',
              color: article.channel_color,
            }}
          >
            {article.channel_professional_type ? t(`screening.types.${article.channel_professional_type}`, { defaultValue: article.channel_name }) : article.channel_name}
          </span>
          <span className="text-xs text-gray-400">
            {formatDistanceToNow(new Date(article.published_at), { addSuffix: true, locale: nl })}
          </span>
          {isExternal && (
            <span className="ml-auto text-gray-300">
              <ExternalLink className="w-3 h-3" />
            </span>
          )}
        </div>

        {isExternal ? (
          <a href={article.external_url!} target="_blank" rel="noopener noreferrer">
            <h3 className="font-bold text-gray-900 line-clamp-2 hover:text-[#0B1F3A] transition-colors leading-snug">
              {article.title}
            </h3>
          </a>
        ) : (
          <Link to={internalHref}>
            <h3 className="font-bold text-gray-900 line-clamp-2 hover:text-[#0B1F3A] transition-colors leading-snug">
              {article.title}
            </h3>
          </Link>
        )}

        {article.excerpt && (
          <p className="text-sm text-gray-500 mt-1.5 line-clamp-2 leading-relaxed">
            {article.excerpt}
          </p>
        )}

        {isExternal && article.external_url && (
          <p className="text-xs text-gray-400 mt-1 truncate">
            {new URL(article.external_url).hostname.replace('www.', '')}
          </p>
        )}

        <div className="flex items-center justify-between mt-3 text-xs text-gray-400">
          <div className="flex items-center gap-1.5">
            {article.profile_photo_path ? (
              <img
                src={`/uploads/${article.profile_photo_path}`}
                alt=""
                className="w-5 h-5 rounded-full object-cover"
              />
            ) : (
              <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center">
                <User className="w-3 h-3 text-gray-400" />
              </div>
            )}
            <span className="text-gray-600 font-medium">
              {article.first_name} {article.last_name}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                likeMutation.mutate();
              }}
              disabled={likeMutation.isPending}
              className={`flex items-center gap-1 transition-colors hover:text-[#0B1F3A] ${
                liked ? 'text-[#0B1F3A]' : 'text-gray-400'
              }`}
              title={liked ? t('newsroom.likeRemove') : t('newsroom.likeAdd')}
            >
              <ThumbsUp className={`w-3.5 h-3.5 ${liked ? 'fill-current' : ''}`} />
              {likesCount}
            </button>
            <span className="flex items-center gap-1">
              <MessageSquare className="w-3.5 h-3.5" /> {article.comments_count}
            </span>
            <span className="flex items-center gap-1">
              <Eye className="w-3.5 h-3.5" /> {article.views_count}
            </span>
          </div>
        </div>
      </div>
    </article>
  );
};

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear - 2023 }, (_, i) => currentYear - i);

export const NewsroomPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [selectedChannelId, setSelectedChannelId] = useState<string | undefined>(undefined);
  const [sortBy, setSortBy] = useState<ArticleSortBy>('recent');
  const [selectedYear, setSelectedYear] = useState<number | undefined>(undefined);
  const [showAllChannels, setShowAllChannels] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const SORT_OPTIONS: { value: ArticleSortBy; label: string; icon: React.ReactNode }[] = [
    { value: 'recent', label: t('newsroom.sortRecent'), icon: <Clock className="w-3.5 h-3.5" /> },
    { value: 'most_read', label: t('newsroom.sortMostRead'), icon: <Eye className="w-3.5 h-3.5" /> },
    { value: 'most_discussed', label: t('newsroom.sortMostDiscussed'), icon: <Flame className="w-3.5 h-3.5" /> },
    { value: 'most_liked', label: t('newsroom.sortMostLiked'), icon: <Star className="w-3.5 h-3.5" /> },
  ];

  const canPost = user?.role === 'professional' || user?.role === 'admin';

  const { data: channels = [], isLoading: channelsLoading } = useQuery({
    queryKey: ['newsroom-channels'],
    queryFn: newsroomApi.getChannels,
  });

  const { data: articles = [], isLoading: articlesLoading } = useQuery({
    queryKey: ['newsroom-articles', activeSearch, selectedChannelId, sortBy, selectedYear],
    queryFn: () =>
      newsroomApi.getArticles({
        search: activeSearch || undefined,
        channel_id: selectedChannelId,
        sort_by: sortBy,
        year: selectedYear,
        limit: 20,
      }),
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setActiveSearch(searchTerm);
  };

  const visibleChannels = showAllChannels ? channels : channels.slice(0, 6);

  return (
    <CommunityLayout>
    <div className="py-8 ml-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-end">
          {canPost && (
            <Button onClick={() => navigate('/newsroom/nieuw-artikel')}>
              <Plus className="w-4 h-4 mr-2" />
              {t('newsroom.postArticle')}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: article grid */}
        <div className="lg:col-span-2 space-y-4">
          {/* Search */}
          <Card>
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('newsroom.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A] focus:border-transparent"
                />
              </div>
              <Button type="submit" variant="outline" size="sm">
                {t('newsroom.search')}
              </Button>
              {activeSearch && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => { setActiveSearch(''); setSearchTerm(''); }}
                >
                  {t('newsroom.clearSearch')}
                </Button>
              )}
            </form>

            {channels.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedChannelId(undefined)}
                  className={`text-sm px-3 py-1.5 rounded-lg transition-colors ${
                    !selectedChannelId
                      ? 'bg-[#0B1F3A] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t('newsroom.allChannels')}
                </button>
                {channels.slice(0, 5).map((channel) => (
                  <button
                    key={channel.id}
                    onClick={() =>
                      setSelectedChannelId(selectedChannelId === channel.id ? undefined : channel.id)
                    }
                    className={`text-sm px-3 py-1.5 rounded-lg transition-colors ${
                      selectedChannelId === channel.id
                        ? 'bg-[#0B1F3A] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {channel.professional_type ? t(`screening.types.${channel.professional_type}`, { defaultValue: channel.name }) : channel.name}
                  </button>
                ))}
              </div>
            )}
          </Card>

          {/* Sort + year filter bar */}
          <div className="flex flex-wrap items-center gap-2">
            <TrendingUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSortBy(opt.value)}
                className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg transition-colors ${
                  sortBy === opt.value
                    ? 'bg-[#0B1F3A] text-white'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-[#0B1F3A] hover:text-[#0B1F3A]'
                }`}
              >
                {opt.icon}
                {opt.label}
              </button>
            ))}
            <select
              value={selectedYear ?? ''}
              onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : undefined)}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:border-[#0B1F3A] focus:outline-none focus:ring-2 focus:ring-[#0B1F3A] cursor-pointer"
            >
              <option value="">{t('newsroom.allYears')}</option>
              {YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          {/* Articles */}
          {articlesLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-lg overflow-hidden animate-pulse">
                  <div className="aspect-video bg-gray-200" />
                  <div className="p-4 space-y-2">
                    <div className="h-3 bg-gray-200 rounded w-1/3" />
                    <div className="h-4 bg-gray-200 rounded w-full" />
                    <div className="h-4 bg-gray-200 rounded w-4/5" />
                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : articles.length === 0 ? (
            <Card>
              <div className="text-center py-12">
                <Newspaper className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                <p className="text-gray-500 font-medium">
                  {activeSearch
                    ? t('newsroom.noArticlesSearch')
                    : selectedYear
                    ? t('newsroom.noArticlesYear', { year: selectedYear })
                    : t('newsroom.noArticles')}
                </p>
                {canPost && (
                  <Button className="mt-4" size="sm" onClick={() => navigate('/newsroom/nieuw-artikel')}>
                    <Plus className="w-4 h-4 mr-2" />
                    {t('newsroom.firstArticle')}
                  </Button>
                )}
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {articles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          )}
        </div>

        {/* Right: channels sidebar */}
        <div className="space-y-4">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <Newspaper className="w-4 h-4 text-[#0B1F3A]" />
                {t('newsroom.channels')}
              </h2>
            </div>

            {channelsLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="space-y-1">
                {visibleChannels.map((channel) => (
                  <button
                    key={channel.id}
                    onClick={() =>
                      setSelectedChannelId(selectedChannelId === channel.id ? undefined : channel.id)
                    }
                    className={`w-full text-left flex items-center justify-between p-2 rounded-lg transition-colors ${
                      selectedChannelId === channel.id
                        ? 'bg-[#0B1F3A] text-white'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <span className="text-sm font-medium truncate">{channel.professional_type ? t(`screening.types.${channel.professional_type}`, { defaultValue: channel.name }) : channel.name}</span>
                    <span
                      className={`text-xs ml-2 flex-shrink-0 ${
                        selectedChannelId === channel.id ? 'text-blue-200' : 'text-gray-400'
                      }`}
                    >
                      {channel.article_count}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {!showAllChannels && channels.length > 6 && (
              <button
                onClick={() => setShowAllChannels(true)}
                className="mt-3 w-full text-sm text-[#0B1F3A] hover:underline flex items-center justify-center gap-1"
              >
                {t('newsroom.showAllChannels', { count: channels.length })} <ChevronRight className="w-3 h-3" />
              </button>
            )}
          </Card>

          <Card>
            <h3 className="font-semibold text-gray-900 mb-3">{t('newsroom.aboutTitle')}</h3>
            <ul className="text-sm text-gray-600 space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-[#0B1F3A] font-bold mt-0.5">·</span>
                {t('newsroom.about1')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#0B1F3A] font-bold mt-0.5">·</span>
                {t('newsroom.about2')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#0B1F3A] font-bold mt-0.5">·</span>
                {t('newsroom.about3')}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#0B1F3A] font-bold mt-0.5">·</span>
                {t('newsroom.about4')}
              </li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
    </CommunityLayout>
  );
};
